import { Button } from 'reactstrap';
import { useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useFormContext } from 'react-hook-form';
import { FaRegTrashCan } from 'react-icons/fa6';
import FormInput from '@/components/forms/FormInput';

import styles from '../blind-structures.module.css';
import { useEffect } from 'react';

const BlindStructureLevelInput = ({ column, row }) => {
  const {
    control,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext();

  const fieldError = errors.levels?.[row.index]?.[column.id];

  const fieldValue = watch(`levels.${row.index}.${column.id}`);

  useEffect(() => {
    trigger(`levels.${row.index}`);
  }, [fieldValue]);

  return (
    <div className="h-100 w-90 d-flex flex-column justify-content-center align-items-center">
      <FormInput
        allowDecimal={false}
        control={control}
        name={`levels.${row.index}.${column.id}`}
        invalid={!!fieldError}
        type="number"
        min={0}
        key={`${row.index}-${column.id}`}
      />
    </div>
  );
};

const BlindStructureLevelsTable = () => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const levels = watch('levels');

  const handleAddLevel = () => {
    setValue('levels', [
      ...levels,
      {
        levelNumber: levels.length + 1,
        smallBlind: 0,
        bigBlind: 0,
        ante: 0,
        smallBet: 0,
        bigBet: 0,
        bringIn: 0,
      },
    ]);
  };

  const handleRemoveLevel = (removeIndex: number) => {
    setValue(
      'levels',
      levels.reduce((acc, curr, index) => {
        if (index === removeIndex) return acc;
        acc.push({
          ...curr,
          levelNumber: acc.length + 1,
        });
        return acc;
      }, []),
    );
  };

  const blindLevelsTable = useReactTable<any>({
    data: levels,
    columns: [
      {
        header: 'Lvl',
        accessorKey: 'levelNumber',
        size: 50,
        cell: ({ row }) => (
          <div className="w-100 h-100 d-flex flex-column justify-content-center align-items-center">
            <p className="m-0">{row.original.levelNumber}</p>
          </div>
        ),
      },
      {
        header: 'Small Blind',
        accessorKey: 'smallBlind',
        meta: {
          comparisonFieldName: 'bigBlind',
          comparisonOperator: '<',
        },
        cell: BlindStructureLevelInput,
      },
      {
        header: 'Big Blind',
        accessorKey: 'bigBlind',
        meta: {
          comparisonFieldName: 'smallBlind',
          comparisonOperator: '>',
        },
        cell: BlindStructureLevelInput,
      },
      {
        header: 'Ante',
        accessorKey: 'ante',
        cell: BlindStructureLevelInput,
      },
      {
        header: 'Small Bet',
        accessorKey: 'smallBet',
        meta: {
          comparisonFieldName: 'bigBet',
          comparisonOperator: '<',
        },
        cell: BlindStructureLevelInput,
      },
      {
        header: 'Big Bet',
        accessorKey: 'bigBet',
        meta: {
          comparisonFieldName: 'smallBet',
          comparisonOperator: '>',
        },
        cell: BlindStructureLevelInput,
      },
      {
        header: 'Bring In',
        accessorKey: 'bringIn',
        cell: BlindStructureLevelInput,
        meta: {
          comparisonFieldName: 'smallBet',
          comparisonOperator: '<',
        },
      },
      {
        id: 'delete',
        size: 50,
        cell: ({ table, row }) => {
          return (
            <div
              className="h-100 w-100 d-flex justify-content-center align-items-center cursor-pointer"
              onClick={() => handleRemoveLevel(row.index)}
            >
              {table.getRowCount() > 1 ? <FaRegTrashCan size={20} /> : null}
            </div>
          );
        },
      },
    ],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: 100,
      },
    },
  });

  const renderErrors = (rowIndex: number) => {
    if (errors?.levels) {
      return (errors?.levels as any)?.map((error: any, index: number) => {
        if (rowIndex !== index) return null;
        return Object.keys(error).map((key) => {
          return <p className="text-danger my-1">{error?.[key]?.message}</p>;
        });
      });
    }
    return null;
  };

  return (
    <>
      <div className={styles.blindLevelsTableBody}>
        <table style={{ borderCollapse: 'separate', borderSpacing: '10px' }}>
          <thead className="border-bottom">
            {blindLevelsTable.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className="pb-2 text-center"
                    style={{ width: header.getSize(), textAlign: 'left' }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {blindLevelsTable.getRowModel().rows.map((row) => (
              <>
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} style={{ width: cell.column.getSize(), textAlign: 'left' }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
                <tr className="w-100">
                  <td colSpan={blindLevelsTable.getAllColumns().length}>{renderErrors(row.index)}</td>
                </tr>
              </>
            ))}
          </tbody>
        </table>
      </div>
      <div className="w-100 d-flex justify-content-between align-items-center flex-row gap-4">
        <div className="w-100 border-bottom h-auto" />
        <Button onClick={handleAddLevel} className="w-100 border" color="link">
          Add New Level
        </Button>
        <div className="w-100 border-bottom h-auto" />
      </div>
    </>
  );
};

export default BlindStructureLevelsTable;
